import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { teamAPI } from "@/api";
import TeamType from "@/constants/TeamType";
import TeamMemberStatusType from "@/constants/TeamMemberStatusType";
import TeamRoleType from "@/constants/TeamRoleType";
import cloneDeep from "lodash/cloneDeep";
import { createLegacyOneOnOneTeamName, deriveOneOnOneName } from "@/lib/teamUtil";

import {
	OneOnOneCreatedEvent, OneOnOneMeetingAssignmentEvent
} from "@/telemetry";

// This helper forces Manager/Report roles to the proper members if the user tries
// to make a non-teaming user the Manager in the relationship
function transformMembers( { members, } ) {
	const manager = members.find( m => m.role === TeamRoleType.MANAGER && m.isRegisteredUser );
	const bothAreTeamingUsers = members.filter( x => x.isRegisteredUser ).length === 2;
	if ( manager && !bothAreTeamingUsers ) {
		const otherAttendee = members.find( m => m.personId !== manager.personId );
		if ( !manager.isRegisteredUser ) {
			otherAttendee.role = TeamRoleType.MANAGER;
			manager.role = TeamRoleType.REPORT;
		}
		otherAttendee.canEdit = false;
		manager.canEdit = false;
	}

	if ( bothAreTeamingUsers ) {
		members.forEach( m => {
			m.canEdit = true;
		} );
	}
	return members;
}

const _state = {
	existingTeamId: null,
	postOneOnOneCreateCallback: null,
	meetingId: null,
	relatedTeamId: null,
	members: [
		/*
			example member:
			{
				personId: "8675309",
				email: "jenny@tutone.com",
				isRegisteredUser: true,
				role: "MANAGER", // or "REPORT", or "PEER"
				name: "Jenny Tutone",
				picture: "https://s.gravatar.com/avatar/12345678"
			}
		*/
	],
	isDialogVisible: false,
	isSavingOneOnOne: false,
};

const getters = {
	isDialogVisible: state => state.isDialogVisible,
	candidateMembers: state => state.members,
	isSavingOneOnOne: state => state.isSavingOneOnOne,
	alreadyExists: state => !!state.existingTeamId,
	willAssignAfterTeamCreation: state => !!state.postOneOnOneCreateCallback,
};

const actions = {
	async initializeWithCandidateOneOnOne( { commit, }, {
		meetingId,
		members,
		personId,
		postOneOnOneCreateCallback,
		existingTeamId,
		relatedTeamId,
	} ) {
		commit( "clearState" );
		commit( "candidateOneOnOneDataLoaded", {
			meetingId,
			members,
			personId,
			postOneOnOneCreateCallback,
			existingTeamId,
			relatedTeamId,
		} );
		commit( "toggleDialogVisibility", true );
	},
	async saveOneOnOneAndAssignToMeeting( { commit, state, rootGetters, } ) {
		commit( "toggleIsSavingOneOnOne", true );
		try {
			Nprogress.start();
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			let teamId = state.existingTeamId;
			if ( !teamId ) {
				const teamPayload = {
					// @deprecated - name is no longer used
					name: "",
					type: TeamType.ONE_ON_ONE,
					relatedTeamId: state.relatedTeamId,
					members: state.members.reduce( ( accum, member ) => {
						accum.push( {
							email: member.email,
							personId: member.personId,
							role: member.role.toUpperCase(),
							status: member.isRegisteredUser ? TeamMemberStatusType.ACTIVE : TeamMemberStatusType.INVITED,
						} );
						return accum;
					}, [] ),
				};
				// NOTE: generating a legacy team name for backwards-compatibility reasons. Fun times.
				teamPayload.name = createLegacyOneOnOneTeamName( teamPayload );
				const team = await teamAPI.createTeam( teamPayload );
				teamId = team.id;

				const teamName = deriveOneOnOneName( teamPayload, userProfile.personId );

				let telemetryEvent = new OneOnOneCreatedEvent( teamName, OneOnOneCreatedEvent.SCHEDULE_VIEW_LOCATION );
				telemetryEvent.submit();

				telemetryEvent = new OneOnOneMeetingAssignmentEvent( teamName, OneOnOneMeetingAssignmentEvent.SCHEDULE_LOCATION );
				telemetryEvent.submit();
			}
			if ( state.postOneOnOneCreateCallback ) {
				state.postOneOnOneCreateCallback( teamId );
			}
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		commit( "toggleIsSavingOneOnOne", false );
		commit( "clearState" );
		Nprogress.done();
	},
};

/**
 * Reactions are really just mutations that are called when an
 *   action invokes the store's `commit()` function. Separating them
 *   here for clarity. They are merged into mutations below.
 */
const reactions = {
	candidateOneOnOneDataLoaded( state, {
		meetingId,
		members,
		personId,
		postOneOnOneCreateCallback,
		existingTeamId,
		relatedTeamId,
	} ) {
		state.meetingId = meetingId;
		state.currentPersonId = personId;
		state.members = [ ...( existingTeamId ? members : transformMembers( { members, } ) ), ];
		state.postOneOnOneCreateCallback = postOneOnOneCreateCallback;
		state.existingTeamId = existingTeamId;
		state.relatedTeamId = relatedTeamId;
	},
	clearState( state ) {
		state.meetingId = null;
		state.members = [];
		state.relatedTeamId = null;
		state.postOneOnOneCreateCallback = () => {};
	},
	toggleDialogVisibility( state, isVisible ) {
		state.isDialogVisible = isVisible;
	},
	toggleIsSavingOneOnOne( state, isSaving ) {
		state.isSavingOneOnOne = isSaving;
	},
	updateMembers( state, { members, } ) {
		state.members = [ ...transformMembers( { members, } ), ];
	},
};

const mutations = {
	...reactions,
	updateMemberRole( state, { personId, role, } ) {
		const members = cloneDeep( state.members );
		const member = members.find( x => x.personId === personId );
		const otherMember = members.find( x => x.personId !== personId );
		if ( role === TeamRoleType.MANAGER ) {
			member.role = role;
			otherMember.role = TeamRoleType.REPORT;
		} else if ( role === TeamRoleType.REPORT ) {
			member.role = role;
			otherMember.role = TeamRoleType.MANAGER;
		} else if ( role === TeamRoleType.PEER ) {
			member.role = role;
			otherMember.role = TeamRoleType.PEER;
		}
		state.members = [ ...transformMembers( { members, } ), ];
	},
};

export default {
	state: _state,
	getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/oneOnOneCreationModal/index.js